import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import "./ButtonList.css";
import ButtonCard from "../ButtonCard/ButtonCard.jsx";
import COMPONENTS from "../../utils/components.js";
import { usePersistentState } from "../../hooks/usePersistentState.jsx";

const useDynamicPositioning = (targetSelector, navSelector) => {
  useEffect(() => {
    const targetElement = document.querySelector(targetSelector);
    const navElement = document.querySelector(navSelector);

    if (!targetElement || !navElement) return;

    const updatePosition = () => {
      const targetRect = targetElement.getBoundingClientRect();
      navElement.style.top = `${targetRect.bottom}px`;
      if (navSelector === ".buttons-card") {
        navElement.style.paddingBottom = `${
          targetRect.bottom + window.scrollY
        }px`;
      }
    };

    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    const mutationObserver = new MutationObserver((mutations) => {
      for (let mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "style" ||
            mutation.attributeName === "class")
        ) {
          updatePosition();
        }
      }
    });

    const config = {
      attributes: true,
      attributeFilter: ["style", "class"],
      subtree: true,
    };

    resizeObserver.observe(targetElement);
    mutationObserver.observe(targetElement, config);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [targetSelector, navSelector]);
};

const ButtonList = ({
  currentUser,
  useVisible,
  saveCepStorage,
  getCepStorage,
  useCepStorage,
  buttons,
  search,
  selectedTag,
}) => {
  const DEFAULT_TAGS = useMemo(
    () => [
      { id: "#EB3A2D", color: "#EB3A2D", name: "Tag 1" },
      { id: "#AFEB2D", color: "#AFEB2D", name: "Tag 2" },
      { id: "#2D8CEB", color: "#2D8CEB", name: "Tag 3" },
    ],
    [],
  );
  const [activeSlide, setActiveSlide] = useVisible("activeSlide");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const scrollContainerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = usePersistentState(
    "buttonsScrollPosition",
    0,
    {
      serializer: JSON.stringify,
      validator: (value) => (typeof value === "number" ? value : 0),
    },
  );
  const scrollPositionRef = useRef(scrollPosition);

  useDynamicPositioning(".tags-container__tags", ".additional__navigation");
  useDynamicPositioning(".additional__navigation", ".sliderContainer");
  useDynamicPositioning(".sliderContainer", ".paragraphs-cards");
  useDynamicPositioning(".paragraphs-cards", ".ease-copy-paste-buttons");
  useDynamicPositioning(".ease-copy-paste-buttons", ".buttons-card");

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    scrollPositionRef.current = scrollPosition;
  }, [scrollPosition]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const nextPosition = container.scrollTop;
      setScrollPosition(nextPosition);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [setScrollPosition]);

  const filteredButtons = useMemo(() => {
    console.log("filter buttons");
    if (search) {
      return buttons.filter((button) =>
        button.name.toLowerCase().includes(search.toLowerCase()),
      );
    }

    return buttons;
  }, [buttons, search]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const savedScroll = scrollPositionRef.current || 0;
    if (container.scrollTop !== savedScroll) {
      container.scrollTop = savedScroll;
    }
  }, [filteredButtons.length, windowWidth]);

  const componentMap = useMemo(() => {
    const map = {};
    Object.keys(COMPONENTS).forEach((key) => {
      map[key.toLowerCase()] = COMPONENTS[key];
    });
    return map;
  }, []);

  const toggleNavigation = (indx) =>
    setActiveSlide(activeSlide === indx ? false : indx);

  return (
    <>
      <section className="buttons-card" ref={scrollContainerRef}>
        {filteredButtons.length ? (
          <ul className="buttons-card__list">
            {filteredButtons.map((button) => {
              const buttonName = button.name
                .toLowerCase()
                .replace(/[^a-zA-Z0-9]+/g, "");
              const ComponentToRender = componentMap[buttonName]; // Получаем компонент из map
              return (
                <ButtonCard
                  currentUser={currentUser}
                  saveCepStorage={saveCepStorage}
                  getCepStorage={getCepStorage}
                  useCepStorage={useCepStorage}
                  useVisible={useVisible}
                  key={button._id}
                  button={button}
                  selectedTag={selectedTag}
                  tags={button.tags && button.tags.length ? button.tags : DEFAULT_TAGS}
                  ComponentToRender={ComponentToRender}
                />
              );
            })}
          </ul>
        ) : null}
      </section>
    </>
  );
};

export default ButtonList;
